import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Chip, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';

import calculateEnabledStatus from '../utils/calculateEnabledStatus';

QuestionnaireAttachment.propTypes = {
  element: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func,
  values: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

function QuestionnaireAttachment(props) {
  const { element, setFieldValue, values, readOnly } = props;

  const fileInputRef = useRef();

  const [notDisabled, setNotDisabled] = useState(true);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupContent, setPopupContent] = useState('');

  useEffect(() => {
    if (element.enableBehavior) {
      const enableStatus = calculateEnabledStatus(element.enableBehavior, element.enableWhen, values);
      setNotDisabled(enableStatus !== undefined && enableStatus);
    }
  }, [element.enableBehavior, element.enableWhen, values]);

  const handleFileChange = (event) => {
    const { files } = event.currentTarget;
    if (files && files.length) {
      const fileDataArray = [];

      const processFile = (index) => {
        if (index >= files.length) {
          // Once all files are processed, update the state
          setFieldValue(element.linkId, [
            ...(values[element.linkId] || []),
            ...fileDataArray,
          ]);
          return;
        }

        const file = files[index];
        const reader = new FileReader();
        reader.onloadend = () => {
          // Get the base64 string
          const base64String = reader.result.split(',')[1];

          // Add this file's data to the array
          fileDataArray.push({
            "contentType": file.type,
            "data": base64String,
            "title": file.name,
          });

          // Process the next file
          processFile(index + 1);
        };
        reader.readAsDataURL(file);
      };

      // Start processing files from the first one
      processFile(0);
    }
  };

  const handleFileClick = (index) => () => {
    const file = values[element.linkId][index];

    // Check if the file is an image
    if (file.contentType.startsWith('image/')) {
      const imageSrc = `data:${file.contentType};base64,${file.data}`;
      setPopupContent(<img src={imageSrc} alt={file.title} style={{ maxWidth: '100%' }} />);
    } else {
      // Decode base64 to binary for non-image files
      const binaryData = atob(file.data);
      const byteNumbers = new Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i += 1) {
        byteNumbers[i] = binaryData.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file.contentType });
      const url = URL.createObjectURL(blob);

      setPopupContent(
        <div>
          <p>{file.title}</p>
          <a href={url} download={file.title}>Download {file.title}</a>
        </div>
      );
    }

    setOpenPopup(true);
  };


  const handleDelete = (index) => () => {
    const newFilesData = [...values[element.linkId]];
    newFilesData.splice(index, 1);
    setFieldValue(element.linkId, newFilesData);
  }

  return (
    <>
      {readOnly ? null : (
        <div>
          <Button
            disabled={!notDisabled}
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current.click()}
            sx={{ mr: 2, minWidth: 200 }}
          >
            {element.required ? `${element.text}*` : element.text}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple={element?.repeats}
            readOnly={readOnly}
            onChange={handleFileChange}
          />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {values[element.linkId]?.map((file, index) => (
          <Chip
            key={index}
            label={file.title}
            onClick={handleFileClick(index)}
            onDelete={readOnly ? undefined : handleDelete(index)}
            style={{ margin: '5px' }}
          />
        ))}
      </div>

      <Dialog open={openPopup} onClose={() => setOpenPopup(false)}>
        <DialogTitle>
          {popupContent.title}
          <IconButton
            aria-label="close"
            onClick={() => setOpenPopup(false)}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {popupContent}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default QuestionnaireAttachment;
